/**
 * Cours Circulation Hub — Style Apple tactile pro
 *
 * Route : /enseignant/seance-circulation
 *
 * Refonte juin 2026 (parité avec PlateauHub) : hero XL, 3 quick-actions
 * (Évaluer / Découvrir canevas / Créer wizard) + filtres REMC C2/C3/C4
 * + grille de fiches premium identique au plateau.
 *
 * Source de vérité : `data/teacher/fichesCirculation.js` (26 fiches REMC).
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bike, ArrowRight, Filter, Sparkles, Layers,
  Wand2, Eye, BookOpen,
  BookCheck, AlignCenter, Gauge, ParkingCircle, CornerUpRight, Crosshair, CircleDot,
  Ruler, ArrowLeftRight, Spline, Megaphone, Users, Truck, Zap, CloudRain, Mountain,
  Map as MapIcon, Compass, AlertTriangle, Siren, ShieldCheck, Wrench, Leaf,
} from 'lucide-react';
import { FICHES_CIRCULATION, CIRCULATION_CATEGORIES } from '../data/teacher/fichesCirculation';
import { hasPremium } from '../data/teacher/fichesInteractives';
import useSeo from '../hooks/useSeo';
import AudienceContextBanner from '../components/AudienceContextBanner';

const ICONS = {
  BookCheck, Eye, AlignCenter, Gauge, ParkingCircle, CornerUpRight, Crosshair, CircleDot,
  Ruler, ArrowLeftRight, Spline, Megaphone, Users, Truck, Zap, CloudRain, Mountain,
  Map: MapIcon, Compass, AlertTriangle, Siren, ShieldCheck, Wrench, Leaf,
};

function FicheCard({ fiche }) {
  const Icon = ICONS[fiche.icon] || Bike;
  const premium = hasPremium(fiche.slug);
  return (
    <Link
      to={`/enseignant/seance-circulation/${fiche.slug}`}
      data-testid={`fiche-card-${fiche.slug}`}
      className="group relative overflow-hidden rounded-3xl bg-card border border-border/60 p-5 sm:p-6 flex flex-col min-h-[200px] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl active:scale-[0.98]"
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -16px rgba(0,0,0,0.12)' }}
    >
      <div
        className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-15 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
        style={{ background: fiche.accent }}
        aria-hidden="true"
      />
      <div
        className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `linear-gradient(90deg, transparent, ${fiche.accent}, transparent)` }}
        aria-hidden="true"
      />
      {premium && (
        <div
          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-black uppercase tracking-widest text-white shadow-lg"
          style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
          data-testid={`badge-premium-${fiche.slug}`}
        >
          <Sparkles className="w-3 h-3" /> Premium
        </div>
      )}
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
            style={{ background: fiche.accent, boxShadow: `0 8px 20px -8px ${fiche.accent}55` }}
          >
            <Icon className="w-6 h-6" strokeWidth={2.2} />
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span
              className="text-[10px] font-black uppercase tracking-[0.14em] rounded-full px-2.5 py-1"
              style={{ background: `${fiche.accent}1a`, color: fiche.accent }}
            >
              {fiche.competence}
            </span>
            <span className="text-[10px] text-muted-foreground font-bold">{fiche.duree}</span>
          </div>
        </div>
        <h3 className="font-black text-base sm:text-lg leading-tight tracking-tight">{fiche.title}</h3>
        <div
          className="text-[10px] font-black uppercase tracking-[0.14em] mt-1.5"
          style={{ color: fiche.accent }}
        >
          {fiche.competenceLabel}
        </div>
        <p className="text-xs sm:text-sm text-muted-foreground mt-2.5 leading-relaxed flex-1">
          {fiche.summary}
        </p>
        <div
          className="flex items-center gap-1.5 text-xs sm:text-sm font-bold mt-4 group-hover:gap-2.5 transition-all duration-300"
          style={{ color: fiche.accent }}
        >
          Ouvrir la fiche <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
        </div>
      </div>
    </Link>
  );
}

export default function CirculationCoursHub() {
  useSeo({
    title: 'Cours Circulation REMC · Bibliothèque C2/C3/C4 · Espace enseignant',
    description:
      'Bibliothèque de 26 fiches séance circulation conformes au livret REMC (catégorie A) : appréhender la route, partager la route, conduire en autonomie. Trame Conduite 4 phases · 9 étapes. Mode présentation full-screen pour donner cours.',
    path: '/enseignant/seance-circulation',
  });

  const [activeCat, setActiveCat] = useState('all');

  const filtered = useMemo(() => {
    if (activeCat === 'all') return FICHES_CIRCULATION;
    return FICHES_CIRCULATION.filter((f) => f.competenceCat === activeCat);
  }, [activeCat]);

  const counts = useMemo(() => {
    const c = { all: FICHES_CIRCULATION.length };
    CIRCULATION_CATEGORIES.forEach((cat) => {
      c[cat.id] = FICHES_CIRCULATION.filter((f) => f.competenceCat === cat.id).length;
    });
    return c;
  }, []);

  return (
    <div className="space-y-6" data-testid="circulation-hub-page">
      {/* HERO style Apple tactile pro — parité avec PlateauHub */}
      <section
        className="relative overflow-hidden rounded-3xl bg-card border border-border/60"
        data-testid="circulation-hero"
        style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 16px 40px -20px rgba(124,58,237,0.25)' }}
      >
        <div
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: '#7c3aed' }}
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl opacity-15 pointer-events-none"
          style={{ background: '#0891b2' }}
          aria-hidden="true"
        />
        <div className="relative p-6 sm:p-9">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#7c3aed]/15 border border-[#7c3aed]/30 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[#7c3aed] mb-4">
            <Bike className="w-3.5 h-3.5" /> Circulation · Appréhender, partager, agir
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            data-testid="circulation-title"
          >
            Donner cours en circulation,<br />
            <span className="text-[#7c3aed]">prêt en 3 clics.</span>
          </h1>
          <p className="text-sm sm:text-base text-foreground/80 mt-3 max-w-3xl leading-relaxed">
            {FICHES_CIRCULATION.length} fiches séance circulation <strong>conformes livret REMC</strong>{' '}
            (C2 appréhender la route · C3 partager la route · C4 conduire en autonomie). Cours · démo ·
            guidage · autonomie · bilan — tout est prêt pour ta séance.
          </p>

          {/* Mini-stats */}
          <div className="mt-5 grid grid-cols-4 gap-2 sm:gap-3 max-w-2xl" data-testid="hero-stats">
            {[
              { num: FICHES_CIRCULATION.length, label: 'Fiches', color: '#7c3aed' },
              { num: counts.C2 || 0, label: 'C2 Appréhender', color: '#0891b2' },
              { num: counts.C3 || 0, label: 'C3 Partager', color: '#16a34a' },
              { num: counts.C4 || 0, label: 'C4 Autonomie', color: '#ea580c' },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-2xl bg-background/40 border border-border/40 backdrop-blur p-2.5 sm:p-3 text-center"
              >
                <div
                  className="text-2xl sm:text-3xl font-black tabular-nums leading-none"
                  style={{ color: s.color, fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {s.num}
                </div>
                <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.14em] text-foreground/65 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Référentiel REMC Compétence 2 — bannière prestige (compacte mobile / riche desktop) */}
      <Link
        to="/enseignant/competence-c2"
        data-testid="cta-competence-c2"
        className="block group relative overflow-hidden rounded-2xl sm:rounded-3xl p-3 sm:p-6 transition-all hover:-translate-y-0.5 hover:shadow-2xl active:scale-[0.99]"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #d97706 100%)',
          boxShadow: '0 10px 30px -10px rgba(217,119,6,0.4)',
        }}
      >
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl bg-amber-400/20 pointer-events-none hidden sm:block" />
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-5 h-5 sm:w-8 sm:h-8 text-white" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[9px] sm:text-[11px] font-black uppercase tracking-[0.08em] sm:tracking-[0.18em] opacity-90 truncate">
              Référentiel REMC · TP-01303
            </div>
            <div
              className="text-sm sm:text-2xl font-black leading-tight mt-0.5"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              <span className="sm:hidden">Compétence 2 · Règles de circulation</span>
              <span className="hidden sm:inline">Compétence 2 — Connaître &amp; respecter les règles de circulation</span>
            </div>
            <p className="hidden sm:block text-xs sm:text-sm opacity-95 mt-1 leading-relaxed">
              8 sous-compétences C2a→C2h · Savoirs · Savoir-faire · Pourquoi · Comment · Risques · Sanctions · Influences · Bilan C2
            </p>
          </div>
          <div className="flex-shrink-0 inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-full bg-white text-[#0f172a] text-[10px] sm:text-sm font-black uppercase tracking-wider shadow-lg group-hover:gap-2 transition-all">
            <span className="hidden sm:inline">Ouvrir</span>
            <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </div>
        </div>
      </Link>

      {/* Bandeau d'adaptation par audience (visible enseignant + futur enseignant) */}
      <AudienceContextBanner
        pageHint="La circulation est l'épreuve comportementale en conditions réelles."
        contexts={{
          enseignant: {
            title: 'Tes fiches pour piloter une séance de circulation',
            bullets: [
              'Fiches REMC C2 (règles) / C3 (partage) / C4 (autonomie) prêtes à l\'emploi.',
              'Mode présentation bord-de-route et schémas SVG visibles à l\'extérieur.',
              'Bascule vers la Séance Live pour guider l\'élève sur l\'itinéraire choisi.',
            ],
            cta: 'Voir les séances live récentes',
            ctaTo: '/enseignant/seances-live',
          },
          'futur-enseignant': {
            title: 'Modèles de fiches circulation pour ton oral CCS',
            bullets: [
              'Analyse comment chaque fiche articule les 4 niveaux de guidage dégressif.',
              'Mémorise les sous-compétences (C2-a → C4-g) et leurs critères observables.',
              'Reproduis la trame ECSR exigée par le jury pour ta présentation.',
            ],
            cta: 'Aller au Hub Préparation CCS',
            ctaTo: '/preparation-ccs',
          },
        }}
        showOnEleve={false}
      />

      {/* QUICK ACTIONS XL — parité Plateau */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="circulation-quick-actions">
        <Link
          to="/enseignant/eval-circulation"
          data-testid="quick-eval-circulation"
          className="group relative overflow-hidden rounded-2xl p-4 sm:p-5 flex items-center gap-3 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            boxShadow: '0 8px 24px -10px #7c3aed77',
          }}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Bike className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.3} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] opacity-85">Évaluer · 50 min</div>
            <div className="text-base sm:text-lg font-black leading-tight">Fiche éval. CCS circulation</div>
          </div>
          <ArrowRight className="w-5 h-5 text-white" strokeWidth={2.5} />
        </Link>
        <Link
          to="/enseignant/canevas-pedagogique"
          data-testid="quick-canevas-circulation"
          className="group relative overflow-hidden rounded-2xl p-4 sm:p-5 flex items-center gap-3 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
            boxShadow: '0 8px 24px -10px #1d4ed877',
          }}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Eye className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.3} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] opacity-85">Découvrir</div>
            <div className="text-base sm:text-lg font-black leading-tight">Voir le canevas type</div>
          </div>
          <ArrowRight className="w-5 h-5 text-white" strokeWidth={2.5} />
        </Link>
        <Link
          to="/enseignant/creer-fiche"
          data-testid="quick-wizard-circulation"
          className="group relative overflow-hidden rounded-2xl p-4 sm:p-5 flex items-center gap-3 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            boxShadow: '0 8px 24px -10px #16a34a77',
          }}
        >
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.3} />
          </div>
          <div className="flex-1 min-w-0 text-white">
            <div className="text-[10px] font-black uppercase tracking-[0.16em] opacity-85">Créer</div>
            <div className="text-base sm:text-lg font-black leading-tight">Ouvrir le wizard</div>
          </div>
          <ArrowRight className="w-5 h-5 text-white" strokeWidth={2.5} />
        </Link>
      </section>

      {/* FILTRES REMC */}
      <section className="rounded-2xl border border-border/60 bg-card p-4" data-testid="circulation-filters">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">
            Filtrer par compétence REMC
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveCat('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider transition-all ${
              activeCat === 'all'
                ? 'bg-foreground text-background shadow-md'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
            data-testid="filter-all"
          >
            Toutes · {counts.all}
          </button>
          {CIRCULATION_CATEGORIES.map((c) => {
            const count = counts[c.id] || 0;
            const disabled = count === 0;
            const isActive = activeCat === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => !disabled && setActiveCat(c.id)}
                disabled={disabled}
                className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider transition-all ${
                  isActive ? 'text-white shadow-md' : 'bg-muted text-muted-foreground hover:bg-muted/70'
                } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                style={isActive ? { background: c.color, boxShadow: `0 4px 12px -4px ${c.color}55` } : undefined}
                data-testid={`filter-${c.id}`}
                title={disabled ? 'Fiches à venir' : c.intent}
              >
                {c.id} · {c.label} · {count}
              </button>
            );
          })}
        </div>
      </section>

      {/* GRILLE FICHES */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center">
          <Layers className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            Aucune fiche pour cette catégorie pour le moment.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((f) => (
            <FicheCard key={f.slug} fiche={f} />
          ))}
        </div>
      )}

      {/* INFO BAS DE PAGE */}
      <div className="rounded-2xl bg-muted/30 border border-border/40 p-4 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-[#7c3aed] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">État actuel :</strong> {FICHES_CIRCULATION.length} fiches livrées
          (C2 · C3 · C4). Les suggestions d&apos;objectifs depuis l&apos;évaluation Circulation pointent
          vers ces fiches quand un slug est disponible, ou vers les contenus Magazine / Enquêtes ETM
          sinon. Pour les <strong>formations spécifiques 7h</strong> (Passerelle A2→A · B→125 · BSR/AM),
          rendez-vous sur <Link to="/enseignant/cours-circulation" className="underline font-bold">Formations spécifiques</Link>.
        </div>
      </div>
    </div>
  );
}
